import * as React from "react";
import * as S from "@styled";
import { IElementDictionary, ISubProcessData, ITaskData } from "processEditor/model/IProcessData";
import { useBeforeunload } from "shared/components/WarnAboutUnsavedChanges";
import CustomModal from "shared/components/CustomModal";
import { Utilities } from "shared/utilities";
import { ChatMessageRole, PromptType, StyledUserSentimentThumbButton, UserSentiment } from "../Modals/AssistantModal";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/cjs/styles/prism";
import useAzureAiSignalR from "processEditor/hooks/UseAzureAiSignalR";
import MonacoEditor from "shared/monaco/components/MonacoEditor";
import { ParameterEditorMode } from "../ParameterInput";
import { sidePanelIcon, thumbDownIcon, thumbUpIcon } from "shared/icons/svgHelpers";
import ReactMarkdown from "react-markdown";
import styled from "styled-components";
import useUserTheme from "shared/styles/hooks/useUserTheme";
import { InlineLoadSpinner } from "shared/components/PageLoadSpinner";
import { HashMap } from "shared/model/HashMap";

export const DocumentationEditor: React.FC<DocumentationEditorProps> = (props) => {
    const [isDirty, setIsDirty] = React.useState(false);
    const [theme] = useUserTheme();
    const [documentationGenerationModalOpen, setDocumentationGenerationModalOpen] = React.useState(false);
    const [documentationInputValue, setDocumentationInputValue] = React.useState('')


    useBeforeunload((handler) => {
        if (isDirty) {
            handler.preventDefault();
            return FRENDS.Localizations.WarningUnsavedChanges;
        }
    });

    const handleToggleContextPanelOpen = () => {
        props.onToggleContextPanel();
    }

    const handleDocumentationInputChange = (documentation: string) => {
        setDocumentationInputValue(documentation);
        if (!isDirty) {
            setIsDirty(true);
        }
    }

    React.useEffect(() => {
        // set initial documentation
        setDocumentationInputValue(props.documentation);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.documentation]);

    const handleCancelClick = () => {
        props.onCancelClick();
    }

    const handleSaveClicked = () => {
        props.onSaveClicked(documentationInputValue)
    }

    const handleToggleDocumentationGenerationModal = () => {
        setDocumentationGenerationModalOpen(prev => !prev)
    }

    const handleUse = (documentation: string) => {
        setDocumentationInputValue(documentation);
        handleToggleDocumentationGenerationModal();
    }
    const isNew = !props.documentation;
    return <S.FlexContainer noWrap={true} asColumn={true} style={{ gap: "1rem", flex: 1 }}>
        <S.FlexContainer style={{ alignItems: "center" }}>
            <S.FlexItem>
                <h2>{isNew ? "Create documentation" : "Edit documentation"}</h2>
            </S.FlexItem>
            <S.FlexContainer style={{ gap: "1rem" }} >
                <S.Button
                    onClick={handleToggleDocumentationGenerationModal}
                    disabled={!!props.parsedContext.error}
                >
                    Generate documentation with Frends assistant
                </S.Button>
                <S.Button
                    variant={S.ButtonType.bordered}
                    title={props.contextPanelOpen ? 'Close context panel' : 'Open context panel'}
                    dangerouslySetInnerHTML={{ __html: sidePanelIcon() }}
                    disabled={!!props.parsedContext.error}
                    onClick={handleToggleContextPanelOpen}
                />
            </S.FlexContainer>
        </S.FlexContainer>
        <S.FlexItem style={{ display: "flex" }}>
            <S.ExpressionEditorContainer>
                <MonacoEditor
                    onInstantChange={handleDocumentationInputChange}
                    value={documentationInputValue}
                    theme={theme}
                    mode={ParameterEditorMode.markdown}
                    key="documentation-editor"
                    height={"100%"}
                />
            </S.ExpressionEditorContainer>
        </S.FlexItem>
        <S.FlexContainer justifyEnd={true} style={{ gap: "1rem" }} >
            <S.Button
                variant={S.ButtonType.bordered}
                onClick={handleCancelClick}
            >
                Cancel
            </S.Button>
            <S.Button
                onClick={handleSaveClicked}
                disabled={!isDirty}
            >
                {props.isNewProcess ? "Ok" : "Save"}
            </S.Button>
        </S.FlexContainer>
        {documentationGenerationModalOpen &&
            <CustomModal
                show={true}
                onClose={handleToggleDocumentationGenerationModal}
                label="bpmn-frends-assistant"
                size="large"
                shouldCloseOnOverlayClick={false}
                modalTitle={<h2>Documentation preview</h2>}
            >
                <DocumentationGenerationView
                    parsedContext={props.parsedContext}
                    onDiscard={handleToggleDocumentationGenerationModal}
                    onUse={handleUse}
                />
            </CustomModal>}
    </S.FlexContainer>
}

const DocumentationGenerationView: React.FC<DocumentationGenerationViewProps> = (props) => {
    const { completionMessage, errorMessage, completionInProgress, startSession, promptCompletion }
        = useAzureAiSignalR<string>(`${Utilities.getUrlBase()}azureAi`, PromptType.DocumentationCompletion);
    const [userSentiment, setUserSentiment] = React.useState<UserSentiment>(UserSentiment.Neutral);
    const sessionId = React.useRef(crypto.randomUUID());

    React.useEffect(() => {
        // Tracks whether the component is still mounted
        // There does not seem to be any way to cancel the signalr Invoke command and async code in useEffect will run to completion even after unmount
        let isMounted = true;

        const runCodeCompletion = async () => {

            try {
                await startSession(sessionId.current);
                await promptCompletion(sessionId.current, PromptType.DocumentationCompletion, props.parsedContext.context, [{ role: ChatMessageRole.User, message: "Generate documentation" }]);
            } catch (e) {
                if (isMounted) {
                    console.log(e);
                    Utilities.toastError('Failed to complete completion request. Please try again. If the problem persists, please contact support@frends.com');
                }
            }
        }
        runCodeCompletion();
        return () => {
            isMounted = false;  // Set to false on unmount to prevent state updates
        };
    }, [])

    const handleUseClick = () => {
        props.onUse(completionMessage!);
    }

    const handleThumbButtonClick = async (clickedSentiment: UserSentiment) => {
        const nextSentiment = userSentiment !== clickedSentiment ? clickedSentiment : UserSentiment.Neutral;
        setUserSentiment(nextSentiment);

        try {
            await Utilities.putJsonAndParseResponse(`api/azureai/prompts/${sessionId.current}/${nextSentiment}`, {});
        } catch (_) {
            Utilities.toastError('Failed to send user sentiment.');
        }
    };


    return completionMessage
        ? <>
            <AiMarkdownResponse message={completionMessage} />
            {completionInProgress
                ? <InlineLoadSpinner top={0} height={40} width={40} />
                : <S.FlexContainer justifySpaceBetween={true} >
                    <S.FlexContainer gap={12}>
                        <StyledUserSentimentThumbButton
                            title="Up vote"
                            dangerouslySetInnerHTML={{ __html: thumbUpIcon('black') }}
                            onClick={() => handleThumbButtonClick(UserSentiment.Positive)}
                            data-voted={userSentiment === UserSentiment.Positive}
                        />
                        <StyledUserSentimentThumbButton
                            title="Down vote"
                            dangerouslySetInnerHTML={{ __html: thumbDownIcon('black') }}
                            onClick={() => handleThumbButtonClick(UserSentiment.Negative)}
                            data-voted={userSentiment === UserSentiment.Negative}
                        />
                    </S.FlexContainer>
                    <S.FlexContainer gap={24}>
                        <S.Button
                            variant={S.ButtonType.bordered}
                            onClick={props.onDiscard}
                        >
                            Discard
                        </S.Button>

                        <S.Button
                            onClick={handleUseClick}
                        >
                            Use
                        </S.Button>
                    </S.FlexContainer>
                </S.FlexContainer>
            }
        </>
        : <span></span>
}

export const AiMarkdownResponse: React.FC<{ message: string, hightLightAllCodeBlocks?: boolean }> = (props) => {
    return <MarkdownWrapper>
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeRaw]}
            components={{
                // eslint-disable-next-line @typescript-eslint/no-unused-vars, @typescript-eslint/no-explicit-any
                code({ node, inline, className, children, ...childProps }: any) {
                    const language = className?.replace(/language-/, ''); // Extract language from className
                    // We don't want to create <code> blocks from all 
                    if (props.hightLightAllCodeBlocks || language == "csharp") {
                        return (
                            <SyntaxHighlighter
                                style={oneDark}
                                PreTag="div"
                                language="csharp"
                                {...childProps}
                            >
                                {String(children).replace(/\n$/, '')}
                            </SyntaxHighlighter>
                        )
                    }
                    return <span> {String(children).replace(/\n$/, '')}</span>;

                },
                table: ({ children }) => (
                    <S.SimpleTable>
                        {children}
                    </S.SimpleTable>
                ),
            }}
        >
            {props.message}
        </ReactMarkdown>
    </MarkdownWrapper>
}
const StyledGenerationActionButtonsContainer = styled.div`
    display: flex;
    gap: 1rem;
    margin-top: 2rem;
    justify-content: flex-end;
`;

const MarkdownWrapper = styled.div`
    h1,h2,h3,h4 {
        margin: 2rem 0 1rem 0;
    }
    p {
        white-space: pre-wrap;
        word-wrap: break-word;
    }
    hr {
        margin: 2rem 0;
    }
`;

interface DocumentationGenerationViewProps {
    parsedContext: ParsedContext;
    onDiscard: () => void;
    onUse: (documenation: string) => void;
}

interface DocumentationEditorProps {
    documentation: string;
    contextPanelOpen: boolean;
    parsedContext: ParsedContext;
    isNewProcess: boolean;
    onCancelClick: () => void;
    onSaveClicked: (documentation: string) => void;
    onToggleContextPanel: () => void;
}

export interface ParsedContext {
    context?: IDocumentationContext;
    error?: string;
}

export interface IDocumentationContext {
    bpmnXml: string;
    elementParameters: IElementDictionary;
}

export interface IDocumentationInitialContext extends IDocumentationContext {
    tasks: HashMap<ITaskData>;
    subprocesses: HashMap<ISubProcessData>;

}


